import { IntentCell, TextCell, TruncatedTextCell, type ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes, type StrictColor } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import type { ExtensionGovernanceUser } from "app/pages/sales-intelligence/modules/extension-management/types";
import { numberSorter, stringSorter } from "app/pages/sales-intelligence/utils/sort";

import { openSetupGuideMailto } from "./buildSetupGuideMailtoUrl";
import {
    ACTIVE_DAYS_WARNING_THRESHOLD,
    STATUS_BG_COLOR_MAP,
    STATUS_LABEL_MAP,
    STATUS_TEXT_COLOR_MAP,
} from "./constants";
import { EmailCell } from "./EmailCell";
import { formatDate } from "./helpers";

export const getGovernanceUsersColumns = (): ColumnsType<ExtensionGovernanceUser> => [
    {
        dataIndex: "userName",
        title: translate("si.management.extension.governance.column.user"),
        sorter: stringSorter("userName"),
        width: 180,
        render: (_value, record) => <TextCell>{record.userName}</TextCell>,
    },
    {
        dataIndex: "userEmail",
        title: translate("si.management.extension.governance.column.email"),
        sorter: stringSorter("userEmail"),
        width: 260,
        render: (_value, record) => <EmailCell email={record.userEmail} />,
    },
    {
        dataIndex: "jobTitle",
        title: translate("si.management.extension.governance.column.job_title"),
        sorter: stringSorter("jobTitle"),
        width: 180,
        render: (_value, record) => <TruncatedTextCell>{record.jobTitle}</TruncatedTextCell>,
    },
    {
        dataIndex: "status",
        title: translate("si.management.extension.governance.column.status"),
        sorter: stringSorter("status"),
        width: 100,
        render: (_value, record) => (
            <IntentCell
                bgColor={STATUS_BG_COLOR_MAP[record.status]}
                label={translate(STATUS_LABEL_MAP[record.status])}
                textColor={STATUS_TEXT_COLOR_MAP[record.status]}
            />
        ),
    },
    {
        dataIndex: "activeDays",
        title: translate("si.management.extension.governance.column.active_days"),
        tooltipTitle: translate("si.management.extension.governance.column.active_days.tooltip"),
        sorter: numberSorter("activeDays"),
        width: 120,
        render: (_value, record) => {
            const { activeDays, status } = record;
            if (status === "NotInstalled") {
                return <TextCell color={colorsPalettes.carbon["250"]}>-</TextCell>;
            }

            let color: StrictColor;

            if (activeDays === 0) {
                color = colorsPalettes.red["400_P"];
            } else if (activeDays < ACTIVE_DAYS_WARNING_THRESHOLD) {
                color = colorsPalettes.orange["300"];
            } else {
                color = colorsPalettes.mint["400"];
            }

            return (
                <TextCell color={color}>
                    {translate("si.management.extension.governance.active_days_value", {
                        count: String(activeDays),
                    })}
                </TextCell>
            );
        },
    },
    {
        dataIndex: "memberSince",
        title: translate("si.management.extension.governance.column.member_since"),
        sorter: stringSorter("memberSince"),
        width: 140,
        render: (_value, record) => <TextCell>{formatDate(record.memberSince)}</TextCell>,
    },
    {
        dataIndex: "userId",
        title: "",
        width: 60,
        render: (_value, record) => {
            if (record.status !== "NotInstalled") {
                return <Box minHeight="37px" />;
            }

            return (
                <Tooltip content={translate("si.management.extension.governance.send_setup_guide")}>
                    <IconButton
                        type="flat"
                        iconName="email"
                        iconSize="xs"
                        data-automation="extension-governance-send-setup-guide"
                        onClick={() => {
                            extensionManagementTrackingService.trackSendSetupGuide();
                            openSetupGuideMailto(record.userEmail);
                        }}
                    />
                </Tooltip>
            );
        },
    },
];
